import { useState, useEffect } from "react";
import { Button, Modal, Form, Container, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

const CONSENT_KEY = "consent";
const CONSENT_VERSION = "1.0";

const ConsentBanner = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [isGdprRegion, setIsGdprRegion] = useState(null);
  const [consent, setConsent] = useState({
    necessary: true,
    analytics: false,
    localStorage: true, // Always true since it's needed for app functionality
  });

  // Load saved consent on mount
  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.version === CONSENT_VERSION && parsed.hasConsented) {
        setShowBanner(false);
        setConsent(parsed);
        applyConsent(parsed);
        return;
      }
    }

    // Show banner while detecting region
    setShowBanner(true);

    // Detect GDPR region
    detectGdprRegion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const detectGdprRegion = async () => {
    try {
      const response = await fetch("https://ipapi.co/json/");
      if (response.ok) {
        const data = await response.json();
        const gdprCountries = [
          "AT",
          "BE",
          "BG",
          "HR",
          "CY",
          "CZ",
          "DK",
          "EE",
          "FI",
          "FR",
          "DE",
          "GR",
          "HU",
          "IE",
          "IT",
          "LV",
          "LT",
          "LU",
          "MT",
          "NL",
          "PL",
          "PT",
          "RO",
          "SK",
          "SI",
          "ES",
          "SE",
          "GB",
          "NO",
          "IS",
          "LI",
        ];

        const isGdpr = gdprCountries.includes(data.country_code);
        setIsGdprRegion(isGdpr);

        // For non-GDPR regions, initialize GA immediately but still show simple notice
        if (!isGdpr) {
          const defaultConsent = {
            necessary: true,
            analytics: true,
            localStorage: true,
          };
          setConsent(defaultConsent);
          applyConsent(defaultConsent);
        }
      }
    } catch (error) {
      // Default to GDPR compliance if detection fails
      setIsGdprRegion(true);
      setShowBanner(true);
    }
  };

  const applyConsent = (consentState) => {
    // Apply Google Analytics consent (Google Consent Mode v2)
    if (window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: consentState.analytics ? "granted" : "denied",
        ad_storage: "denied", // We don't use ads
        ad_user_data: "denied", // We don't collect user data for ads
        ad_personalization: "denied", // We don't use personalized ads
        functionality_storage: "granted", // localStorage is necessary
        personalization_storage: "denied", // We don't use personalization tracking
      });
    }

    // Initialize GA if analytics consent is granted
    if (consentState.analytics && window.gtag) {
      window.gtag("config", "G-QXWQLYT7NW");
    }
  };

  const saveConsent = (newConsent) => {
    const consentData = {
      ...newConsent,
      hasConsented: true,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    };

    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    applyConsent(consentData);
    setConsent(newConsent);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const acceptAll = () => {
    const allConsent = {
      necessary: true,
      analytics: true,
      localStorage: true,
    };
    saveConsent(allConsent);
  };

  const acceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      localStorage: true,
    };
    saveConsent(necessaryOnly);
  };

  const handlePreferenceChange = (type) => {
    setConsent((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const savePreferences = () => {
    saveConsent(consent);
  };

  const handleGotIt = () => {
    // For non-GDPR regions, just dismiss the banner
    const consentData = {
      ...consent,
      hasConsented: true,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    };

    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    setShowBanner(false);
  };

  if (!showBanner && !showPreferences) return null;

  return (
    <>
      {/* Consent Banner */}
      {showBanner && !showPreferences && (
        <div
          className="position-fixed bottom-0 start-0 end-0 bg-white border-top shadow-lg p-3 p-md-4"
          style={{ zIndex: 1040 }}
        >
          <Container>
            <Row className="align-items-center">
              <Col lg={isGdprRegion === false ? 10 : 8}>
                <p className="mb-0 text-muted">
                  {isGdprRegion === false
                    ? t("consent.banner.simpleDescription")
                    : t("consent.banner.description")}
                  <span className="ms-2 small text-muted">
                    {t("consent.banner.learnMore")}{" "}
                    <a
                      href="https://onbookmarks.com/legal/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none"
                    >
                      {t("consent.banner.privacyPolicy")}
                    </a>
                    .
                  </span>
                </p>
              </Col>
              <Col lg={isGdprRegion === false ? 2 : 4} className="mt-3 mt-lg-0">
                <div className="d-flex gap-2 flex-wrap justify-content-lg-end justify-content-center">
                  {isGdprRegion === false ? (
                    // Simple notice for non-GDPR regions
                    <Button
                      variant="primary"
                      className="h-min-auto"
                      onClick={handleGotIt}
                    >
                      {t("consent.banner.gotIt")}
                    </Button>
                  ) : (
                    // Full GDPR options for EU regions
                    <>
                      <Button
                        variant="white"
                        className="h-min-auto"
                        onClick={() => setShowPreferences(true)}
                      >
                        {t("consent.banner.preferences")}
                      </Button>
                      <Button
                        variant="primary"
                        className="h-min-auto"
                        onClick={acceptAll}
                      >
                        {t("consent.banner.acceptAll")}
                      </Button>
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      )}

      {/* Preferences Modal */}
      <Modal
        show={showPreferences}
        onHide={() => setShowPreferences(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("consent.preferences.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">
            {t("consent.preferences.description")}
          </p>

          {/* Essential Storage */}
          <div className="border rounded p-3 mb-4">
            <div className="d-flex align-items-start justify-content-between">
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i className="bi bi-check-circle-fill text-success"></i>
                  <h6 className="mb-0">
                    {t("consent.categories.essential.title")}
                  </h6>
                  <span className="badge bg-secondary">
                    {t("consent.categories.essential.badge")}
                  </span>
                </div>
                <p className="text-muted small mb-2">
                  {t("consent.categories.essential.description")}
                </p>
                <details className="small text-muted">
                  <summary
                    className="text-primary"
                    style={{ cursor: "pointer" }}
                  >
                    {t("consent.categories.showDetails")}
                  </summary>
                  <ul className="mt-2">
                    <li>{t("consent.categories.essential.localStorage")}</li>
                    <li>{t("consent.categories.essential.indexedDB")}</li>
                    <li>{t("consent.categories.essential.session")}</li>
                  </ul>
                </details>
              </div>
              <Form.Check
                type="switch"
                checked={true}
                disabled
                className="ms-3"
              />
            </div>
          </div>

          {/* Analytics */}
          <div className="border rounded p-3">
            <div className="d-flex align-items-start justify-content-between">
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i className="bi bi-bar-chart-fill text-info"></i>
                  <h6 className="mb-0">
                    {t("consent.categories.analytics.title")}
                  </h6>
                </div>
                <p className="text-muted small mb-2">
                  {t("consent.categories.analytics.description")}
                </p>
                <details className="small text-muted">
                  <summary
                    className="text-primary"
                    style={{ cursor: "pointer" }}
                  >
                    {t("consent.categories.showDetails")}
                  </summary>
                  <ul className="mt-2">
                    <li>
                      Google Analytics -{" "}
                      {t("consent.categories.analytics.googleAnalytics")}
                    </li>
                  </ul>
                </details>
              </div>
              <Form.Check
                type="switch"
                id="analytics-switch"
                checked={consent.analytics}
                onChange={() => handlePreferenceChange("analytics")}
                className="ms-3"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={acceptNecessary}>
            {t("consent.preferences.necessaryOnly")}
          </Button>
          <Button variant="primary" onClick={savePreferences}>
            {t("consent.preferences.savePreferences")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ConsentBanner;
